import { useCallback, useEffect, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { ADMIN, hasProfile } from '../../app/auth/accessPolicy';
import NotificationService from '../../sdk/services/NotificationService';
import ForumService from '../../sdk/services/ForumService';
import type { AppNotification } from '../../sdk/@types/Notificacao';
import type { ModerationStats } from '../../sdk/@types/Forum';
import { readApiError, type ApiError } from '../../sdk/errors/apiError';
import { useProfileStore } from '../store/useProfileStore';
import { useModerationCountersStore } from '../store/useModerationCountersStore';
import {
  acquireNotificationReads,
  applyNotificationsPatch,
  bumpNotificationReads,
  EMPTY_ITEMS,
  ensureNotificationReads,
  useNotificationReadsStore,
} from '../store/useNotificationReadsStore';
import useDueCount from './useDueCount';
import useSessionKey from './useSessionKey';

/**
 * As três fontes do sino, lidas juntas: notificações do usuário, cartões
 * vencendo hoje e — só para perfil administrativo — a fila de moderação.
 *
 * `total` é a soma do que pede atenção **no sino**:
 * `unread + (admin ? pendingComments + openReports : 0)`. Desde
 * `app-shell-pencil-fidelity`, `due` não entra mais nessa soma — tem
 * superfície própria (a pill de revisão do Topbar) — mas continua voltando
 * no objeto, inalterado, para o bloco "Revisão" do painel.
 */
export interface UseNotifications {
  /** as notificações devolvidas por `GET /notifications`, mais recentes primeiro */
  items: AppNotification[];
  /**
   * não lidas do usuário, **vindas da API** (`unread`), nunca
   * `items.filter(...).length`: a lista pode estar filtrada ou cortada.
   */
  unread: number;
  /** cartões vencendo hoje — `dueToday`, via `useDueCount` */
  due: number;
  /** fila do moderador, ou `null` para quem não é admin (nem foi lida) */
  moderation: ModerationStats | null;
  /**
   * 403/404 reconhecido de `GET /moderation/stats`, para quem precisa mostrar
   * o próprio painel de negativa (a view de moderação) em vez de só deixar o
   * badge sumir. `null` fora desse caso — inclusive quando o erro não é uma
   * negativa reconhecida, que degrada em silêncio como as outras fontes.
   */
  moderationDenial: ApiError | null;
  /** o número do badge; zero esconde o badge */
  total: number;
  /** marca uma não lida; já lida não dispara chamada */
  markRead: (id: string) => Promise<void>;
  /** marca todas as não lidas, uma a uma */
  markAllRead: () => Promise<void>;
  /**
   * Decrementa `pendingComments`/`openReports` na hora, sem esperar a próxima
   * navegação — chamada por quem acabou de aprovar/rejeitar/concluir e sabe
   * quantos itens saíram da fila. O ajuste é **compartilhado**: como o contador
   * mora em `useModerationCountersStore`, o sino e o badge do Sidebar (cada um
   * com a sua própria instância deste hook) refletem o novo número
   * imediatamente, mesmo sem trocar de rota.
   */
  applyModerationDelta: (delta: { pendingComments?: number; openReports?: number }) => void;
  /** relê notificações e moderação (a contagem de revisão segue a navegação) */
  reload: () => void;
  loading: boolean;
  /** erro de qualquer fonte, exposto em vez de lançado */
  error: Error | null;
}

/** Preserva o erro original (o interceptor pendura `apiError` nele). */
function toError(raw: unknown): Error {
  return raw instanceof Error ? raw : new Error(String(raw));
}

/**
 * Contadores do sino do header — notificações + revisão + fila de moderação,
 * relidos na montagem e a cada mudança de `location.pathname`.
 *
 * Cinco decisões que não são óbvias:
 *
 * 1. **A contagem de revisão vem do `useDueCount` existente**, consumido aqui,
 *    não reimplementada — e, desde `app-shell-pencil-fidelity`, não soma mais
 *    em `total`: `due` só alimenta o bloco "Revisão" do painel, a pill do
 *    Topbar é que mostra o número agora. A nota daquele hook é que a leitura
 *    tem de ser `getStats` e **não** `getDue` (que materializa templates de
 *    admin como efeito colateral, algo que o header não pode disparar a cada
 *    página). Copiar a chamada para dentro deste hook seria a forma mais fácil
 *    de perder essa nota, e chamar `getStats` duas vezes por navegação seria
 *    pior.
 * 2. **`GET /moderation/stats` só sai para perfil `administrador`/`master`**,
 *    lido do `useProfileStore`. Sem esse gate todo aluno logado bateria numa
 *    rota `requireAdminOrMaster` a cada navegação e o interceptor global
 *    viraria o 403 num **toast de negativa por troca de página**. O perfil vem
 *    da semente do `localStorage` quando ela existe, e quando não existe **o
 *    hook o busca**: sem isso, num recarregamento em `/carrossel` (rota
 *    `logged()`, que não passa por `RequireProfile`) o perfil nunca
 *    resolveria e o moderador não veria badge algum justamente na tela onde
 *    ele passa o dia. Por isso a fila de moderação tem efeito próprio — ela
 *    espera o perfil; notificações e revisão, não.
 * 3. **Polling na navegação, nunca socket.** `/api/ws` é escopado a simulado:
 *    devolve 404 para quem não tem simulado em curso e o Hub revalida esse
 *    registro em cada broadcast, então ele desconectaria o usuário em vez de
 *    entregar o badge. Nada aqui abre conexão.
 * 4. **Notificações e revisão são deduplicadas entre instâncias**
 *    (`useNotificationReadsStore`); **moderação não é.** O sino e o Sidebar
 *    dividem uma única leitura de `/notifications` e `/review/stats` por
 *    navegação. A fila de moderação, ao contrário, é buscada **por
 *    instância montada** — cada `useNotifications()` faz a sua própria
 *    `GET /moderation/stats` —, mas o *resultado* mora em
 *    `useModerationCountersStore`, compartilhado: as leituras convergem para
 *    o mesmo número assim que chegam, e `applyModerationDelta` derruba todo
 *    mundo no mesmo render, sem esperar navegação. Juntar moderação ao
 *    dedup de notificações faria duas instâncias disputarem uma única
 *    leitura — quebrando o caso "sino e Sidebar cada um com a sua contagem,
 *    ajustada em conjunto por quem decide" que motivou o store separado.
 * 5. **Nada sobrevive à troca de usuário na mesma aba.** O estado de
 *    notificações/revisão é de módulo, etiquetado com a sessão
 *    (`useSessionKey`) e volta ao zero quando o último consumidor desmonta. O
 *    "Sair" do Sidebar só navega para `/login`, sem limpar o storage nem
 *    recarregar o documento, então um segundo login pode acontecer com este
 *    hook montado; enquanto a etiqueta não bater com o token de agora, o hook
 *    devolve lista vazia e contagem zero, e relê.
 *
 * Nenhuma falha é lançada: erro de qualquer fonte vai para `error` e o painel
 * continua renderizável. O erro da revisão é a exceção documentada — a leitura
 * de `dueToday` degrada para zero por conta própria, e isso continua sendo do
 * `useDueCount`.
 */
export default function useNotifications(enabled = true): UseNotifications {
  const location = useLocation();
  const profile = useProfileStore((state) => state.profile);
  const profileStatus = useProfileStore((state) => state.status);
  const loadProfile = useProfileStore((state) => state.load);

  // Lido a cada render: um login/logout na mesma aba muda esta chave e a
  // leitura reage, sem depender de um evento de storage (que nem dispara na
  // mesma aba).
  const session = useSessionKey();
  const active = enabled && !!session;
  const isAdmin = hasProfile(profile, ADMIN);

  const due = useDueCount(active);

  /**
   * Aquece o cache de perfil quando ninguém mais o aqueceu.
   *
   * `RequireProfile` chama `load()` ao guardar uma rota — mas `/carrossel` e
   * `/perfil` são `logged()`, não `gated()`, e é justamente no carrossel que o
   * moderador passa o dia. Num recarregamento com token e **sem** a semente
   * `userProfile` no `localStorage`, o perfil ficaria `null`/`idle` para
   * sempre: o bloco Moderação nunca apareceria e o badge nunca somaria a fila.
   *
   * É o mesmo remendo que `QuestionForum` já faz pelo mesmo motivo, e não
   * acrescenta round-trip: `load()` dedupa em escopo de módulo (`inFlight`
   * junta chamadas simultâneas, `loadedOnce` impede a segunda), e a leitura em
   * si é a compartilhada com o menu (`auth/loggedUser.ts`). Dois consumidores
   * deste hook — o sino e o Sidebar — mais as guardas dão **uma** chamada.
   */
  useEffect(() => {
    if (active && profileStatus === 'idle') loadProfile();
  }, [active, profileStatus, loadProfile]);

  /* ------------------------------------------------ notificações + revisão */

  const storeSession = useNotificationReadsStore((state) => state.session);
  const storeItems = useNotificationReadsStore((state) => state.items);
  const storeUnread = useNotificationReadsStore((state) => state.unread);
  const notificationsLoading = useNotificationReadsStore((state) => state.loadingNotifications);
  const storeError = useNotificationReadsStore((state) => state.error);
  // Só para o efeito abaixo reagir a `reload()`: `bumpNotificationReads()`
  // incrementa este `tick` no store, e é ele que abre a chave nova que
  // `ensureNotificationReads` usa para decidir se relê. Sem esta assinatura,
  // o efeito não tem por que rodar de novo — `reload()` bumparia o tick à
  // toa e nenhuma segunda `GET /notifications` sairia.
  const storeTick = useNotificationReadsStore((state) => state.tick);

  /**
   * A sessão a que o estado compartilhado pertence, comparada **durante o
   * render**: assim a troca de usuário não chega a pintar um frame com os
   * dados do anterior, coisa que um `setState` dentro do efeito (que roda
   * depois do render) não impediria.
   */
  const stale = storeSession !== session;

  // Enquanto alguém consome, o estado compartilhado vive; quando o último
  // desmonta, ele volta ao zero — nada de notificação sobrevivendo à árvore.
  useEffect(() => acquireNotificationReads(), []);

  useEffect(() => {
    if (!active) return;
    ensureNotificationReads({ session, pathname: location.pathname });
    // `location.pathname` é a releitura na navegação; `session`, a troca de
    // usuário sem recarregar o documento; `storeTick`, o `reload()` — sem lê-lo
    // aqui o efeito não saberia que uma chave nova foi aberta no store.
  }, [active, session, location.pathname, storeTick]);

  const safeItems = active && !stale ? storeItems : EMPTY_ITEMS;
  const safeUnread = active && !stale ? storeUnread : 0;

  /* --------------------------------------------------------- moderação */

  const moderationStats = useModerationCountersStore((state) => state.stats);
  const moderationDenialState = useModerationCountersStore((state) => state.denial);
  const setModerationStats = useModerationCountersStore((state) => state.setStats);
  const setModerationDenial = useModerationCountersStore((state) => state.setDenial);
  const applyModerationDelta = useModerationCountersStore((state) => state.applyDelta);

  const [moderationLoading, setModerationLoading] = useState(false);
  const [moderationError, setModerationError] = useState<Error | null>(null);
  /** Bumped por `reload()` — a única forma de relê a fila sem navegação. */
  const [moderationTick, setModerationTick] = useState(0);

  useEffect(() => {
    if (!active || !isAdmin) {
      // Aluno, perfil ainda não resolvido, ou deixou de ser admin nesta
      // sessão: a fila não fica pendurada na tela.
      setModerationStats(null);
      setModerationDenial(null);
      setModerationLoading(false);
      return;
    }

    let cancelled = false;
    setModerationLoading(true);
    setModerationError(null);

    ForumService.getModerationStats()
      .then((stats) => {
        if (cancelled) return;
        setModerationStats(stats ?? null);
        setModerationDenial(null);
      })
      .catch((raw) => {
        if (cancelled) return;
        setModerationError(toError(raw));
        // Só uma negativa reconhecida (403/404) vira painel de bloqueio; o
        // resto degrada em silêncio, como as outras fontes.
        const apiError = readApiError(raw);
        setModerationDenial(apiError.kind ? apiError : null);
      })
      .then(() => {
        if (!cancelled) setModerationLoading(false);
      });

    return () => {
      cancelled = true;
    };
    // Mesmos gatilhos da leitura de notificações, mais `isAdmin` (a resolução
    // tardia do perfil) e `moderationTick` (o `reload()` desta fonte).
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [active, isAdmin, session, location.pathname, moderationTick]);

  const safeModeration = stale || !isAdmin ? null : moderationStats;
  const safeModerationDenial = stale || !isAdmin ? null : moderationDenialState;

  const moderationTotal = safeModeration
    ? safeModeration.pendingComments + safeModeration.openReports
    : 0;

  /* --------------------------------------------------------------- ações */

  const markRead = useCallback(
    async (id: string) => {
      const before = useNotificationReadsStore.getState();
      // A lista em memória é de outra sessão: não é o que o usuário está
      // vendo, e marcá-la como lida marcaria a notificação de outro usuário.
      if (before.session !== session) return;

      const target = before.items.find((item) => item.id === id);
      // Já lida (ou fora da lista): nenhuma requisição.
      if (!target || target.readAt !== null) return;

      const previousUnread = before.unread;
      const optimisticReadAt = new Date().toISOString();

      const patch = (readAt: string | null, merge?: Partial<AppNotification>) =>
        applyNotificationsPatch({
          items: useNotificationReadsStore
            .getState()
            .items.map((item) => (item.id === id ? { ...item, ...merge, id: item.id, readAt } : item)),
        });

      patch(optimisticReadAt);

      try {
        const result = await NotificationService.markRead(id);
        // A contagem é a que a API devolveu, não um decremento local: ela é a
        // única que conhece as não lidas fora desta página. Como o estado é
        // compartilhado, o badge do sino e o do Sidebar caem juntos, sem
        // segunda leitura de `GET /notifications`.
        applyNotificationsPatch({ unread: result.unread });
        patch(result.notification.readAt ?? optimisticReadAt, result.notification);
      } catch (raw) {
        patch(target.readAt);
        applyNotificationsPatch({ unread: previousUnread });
        useNotificationReadsStore.setState((state) => ({
          error: state.error ?? toError(raw),
        }));
      }
    },
    [session]
  );

  const markAllRead = useCallback(async () => {
    const pending = useNotificationReadsStore.getState().items.filter((item) => item.readAt === null);
    // Em série: a contagem final é a da última resposta, e não uma corrida
    // entre respostas concorrentes que discordam entre si.
    for (const item of pending) {
      // eslint-disable-next-line no-await-in-loop
      await markRead(item.id);
    }
  }, [markRead]);

  const reload = useCallback(() => {
    bumpNotificationReads();
    setModerationTick((tick) => tick + 1);
  }, []);

  return {
    items: safeItems,
    unread: safeUnread,
    due,
    moderation: safeModeration,
    moderationDenial: safeModerationDenial,
    total: safeUnread + moderationTotal,
    markRead,
    markAllRead,
    applyModerationDelta,
    reload,
    // A releitura da sessão nova já começou: é carregamento, não lista vazia.
    loading: notificationsLoading || moderationLoading || (stale && active),
    error: stale ? null : (storeError ?? moderationError),
  };
}
